'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { diffWords } from 'diff';
import { useTranslations } from 'next-intl';
import { Box, VStack, css } from '@terra/ui-styled-system';
import { BodyDefault } from '@terra/ui-design-system';
import type {
  FindingDraftUpdateEffect,
  PendingAiFieldEdit,
} from '../../store/findingEditStore';
import {
  getFieldUpdateHighlightProps,
  useFieldUpdateHighlight,
} from '../../hooks/useFieldUpdateHighlight';
import { useCopilotReviewArrival } from '../../hooks/useCopilotReviewArrival';
import {
  areFindingSuggestionValuesEqual,
  FINDING_SUGGESTION_CHROME_KIND,
  formatFindingSuggestionValue,
  getFindingSuggestionChromeKind,
  isFindingSuggestionAtBaseline,
} from '../../utils/findingSuggestionFieldUtils';
import { FindingCopilotNote } from './FindingCopilotNote';
import { ObjectContextSelectionToolbar } from './ObjectContextSelectionToolbar';

const copilotReviewControlStyles = css({
  '&[data-copilot-review-ring="true"]': {
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'border.accent.default',
    borderRadius: 'md',
    overflow: 'visible',
    transition: 'border-color 220ms ease',
    _focusWithin: {
      borderColor: 'border.accent.default',
    },
    '& :is(.input, .textarea, .select__trigger, .select__control)': {
      borderWidth: '0',
      borderColor: 'transparent',
      borderRadius: 'md',
      boxShadow: 'none',
      outline: 'none',
      _hover: {
        borderWidth: '0',
        borderColor: 'transparent',
        boxShadow: 'none',
      },
      _focusVisible: {
        borderWidth: '0',
        borderColor: 'transparent',
        boxShadow: 'none',
        outline: 'none',
      },
      _active: {
        borderWidth: '0',
        borderColor: 'transparent',
        boxShadow: 'none',
      },
      '&[data-focus-visible]': {
        borderWidth: '0',
        borderColor: 'transparent',
        boxShadow: 'none',
        outline: 'none',
      },
    },
    '& [data-copilot-review-inline-control="true"]': {
      borderWidth: '0',
      borderColor: 'transparent',
      borderRadius: 'md',
      boxShadow: 'none',
      outline: 'none',
    },
  },
});

type ObjectContextMarkableFieldProps = {
  field: string;
  fieldLabel: string;
  fullText: string;
  sendToChatLabel: string;
  updateEffect?: FindingDraftUpdateEffect;
  pendingEdit?: PendingAiFieldEdit;
  isReviewing?: boolean;
  reviewArrivalAt?: number;
  isAcceptedFlash?: boolean;
  currentValue?: unknown;
  baselineValue?: unknown;
  formatPendingValue?: (value: unknown) => string;
  onAcceptPendingEdit?: () => void;
  onDiscardPendingEdit?: () => void;
  onActivatePendingTextEdit?: () => void;
  onSendSelection: ({
    field,
    fieldLabel,
    excerpt,
    lineStart,
    lineEnd,
  }: {
    field: string;
    fieldLabel: string;
    excerpt: string;
    lineStart?: number;
    lineEnd?: number;
  }) => void;
  children: React.ReactNode;
};

export function ObjectContextMarkableField({
  field,
  fieldLabel,
  fullText,
  sendToChatLabel,
  updateEffect,
  pendingEdit,
  isReviewing = false,
  reviewArrivalAt,
  isAcceptedFlash,
  currentValue,
  baselineValue,
  formatPendingValue,
  onAcceptPendingEdit,
  onDiscardPendingEdit,
  onActivatePendingTextEdit,
  onSendSelection,
  children,
}: ObjectContextMarkableFieldProps) {
  const t = useTranslations(
    'designSystem.findings.editDrawer.suggestionReview',
  );
  const fieldRef = useRef<HTMLDivElement>(null);
  const isAnimating = useFieldUpdateHighlight({
    field,
    updateEffect: isAcceptedFlash ? undefined : updateEffect,
  });
  const highlightProps = getFieldUpdateHighlightProps(isAnimating);
  const { washPortal } = useCopilotReviewArrival({
    isReviewing: isReviewing && !isAcceptedFlash,
    reviewArrivalAt:
      isReviewing && !isAcceptedFlash ? reviewArrivalAt : undefined,
    elementRef: fieldRef,
  });
  const hasPendingChrome = Boolean(
    pendingEdit && onAcceptPendingEdit && onDiscardPendingEdit,
  );
  const chromeKind = pendingEdit
    ? getFindingSuggestionChromeKind(pendingEdit.field)
    : 'text';
  const [isInlineDiffActive, setIsInlineDiffActive] = useState(true);
  const formatValue = useMemo(
    () =>
      formatPendingValue ??
      ((value: unknown) => formatFindingSuggestionValue({ value })),
    [formatPendingValue],
  );
  const isCurrentMatchingAfter =
    pendingEdit && currentValue !== undefined
      ? areFindingSuggestionValuesEqual({
          left: currentValue,
          right: pendingEdit.after,
        })
      : true;
  const showInlineDiff =
    hasPendingChrome &&
    chromeKind === FINDING_SUGGESTION_CHROME_KIND.text &&
    isInlineDiffActive &&
    isCurrentMatchingAfter;
  const inlineDiffSegments = useMemo(() => {
    if (!pendingEdit) {
      return [];
    }

    return diffWords(
      formatValue(pendingEdit.before),
      formatValue(pendingEdit.after),
    );
  }, [formatValue, pendingEdit]);

  useEffect(() => {
    setIsInlineDiffActive(true);
  }, [pendingEdit?.after, pendingEdit?.before, pendingEdit?.field]);

  const activatePendingTextEditor = () => {
    if (
      pendingEdit &&
      chromeKind === FINDING_SUGGESTION_CHROME_KIND.text &&
      onActivatePendingTextEdit &&
      isFindingSuggestionAtBaseline({
        current: currentValue,
        before: pendingEdit.before,
        original: baselineValue,
      })
    ) {
      onActivatePendingTextEdit();
    }

    setIsInlineDiffActive(false);
  };

  const showReviewRing = isReviewing && !isAcceptedFlash;

  const showSuggestionChrome =
    (hasPendingChrome &&
      pendingEdit &&
      onAcceptPendingEdit &&
      onDiscardPendingEdit) ||
    isAcceptedFlash;

  const suggestionChrome = (() => {
    if (isAcceptedFlash) {
      return <FindingCopilotNote variant="accepted" />;
    }

    if (!pendingEdit || !onAcceptPendingEdit || !onDiscardPendingEdit) {
      return null;
    }

    if (chromeKind === FINDING_SUGGESTION_CHROME_KIND.select) {
      return (
        <FindingCopilotNote
          variant="suggested-select"
          beforeLabel={formatValue(pendingEdit.before) || t('emptyValue')}
          afterLabel={formatValue(pendingEdit.after) || t('emptyValue')}
          isFocused={isReviewing}
          onAccept={onAcceptPendingEdit}
          onDiscard={onDiscardPendingEdit}
        />
      );
    }

    return (
      <FindingCopilotNote
        variant="suggested-text"
        isFocused={isReviewing}
        onAccept={onAcceptPendingEdit}
        onDiscard={onDiscardPendingEdit}
      />
    );
  })();

  const controlContent =
    showInlineDiff && pendingEdit ? (
      <Box
        as="button"
        w="full"
        textAlign="left"
        borderWidth={showReviewRing ? '0' : '1px'}
        borderColor={showReviewRing ? 'transparent' : 'border.default'}
        borderRadius="md"
        bg="bg.default"
        px={3}
        py={2}
        minH="10"
        cursor="text"
        data-copilot-review-inline-control={showReviewRing ? 'true' : undefined}
        onClick={activatePendingTextEditor}
        onFocus={activatePendingTextEditor}
      >
        <BodyDefault as="span" whiteSpace="pre-wrap" wordBreak="break-word">
          {inlineDiffSegments.map((segment, index) => {
            if (segment.removed) {
              return (
                <Box
                  key={`removed-${index}`}
                  as="span"
                  color="fg.neutral.muted"
                  textDecoration="line-through"
                >
                  {segment.value}
                </Box>
              );
            }

            return (
              <Box key={`kept-${index}`} as="span" color="fg.default">
                {segment.value}
              </Box>
            );
          })}
        </BodyDefault>
      </Box>
    ) : (
      <ObjectContextSelectionToolbar
        field={field}
        fieldLabel={fieldLabel}
        fullText={fullText}
        sendToChatLabel={sendToChatLabel}
        onSendSelection={onSendSelection}
      >
        {children}
      </ObjectContextSelectionToolbar>
    );

  return (
    <Box
      ref={fieldRef}
      data-field-anchor={field}
      data-pending-review-field={hasPendingChrome ? field : undefined}
      w="full"
    >
      {washPortal}
      <VStack gap={1.5} alignItems="stretch" w="full">
        <Box
          borderRadius="md"
          borderWidth={isAnimating ? highlightProps.borderWidth : '0'}
          borderStyle="solid"
          borderColor={highlightProps.borderColor}
          bg={highlightProps.bg}
          boxShadow={highlightProps.boxShadow}
          transition={highlightProps.transition}
          animation={highlightProps.animation}
          px={isAnimating ? 2 : 0}
          py={isAnimating ? 2 : 0}
          w="full"
        >
          <Box
            className={copilotReviewControlStyles}
            data-copilot-review-ring={showReviewRing ? 'true' : undefined}
            w="full"
            borderRadius={showReviewRing ? undefined : 'l2'}
          >
            {controlContent}
          </Box>
        </Box>
        {showSuggestionChrome ? suggestionChrome : null}
      </VStack>
    </Box>
  );
}
